import { useState } from 'react';
import { Plus, FileText, Home, MoreVertical, Trash2, Edit2 } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  useToast,
} from '@mawkaa/ui';
import { useBuilderStore } from '@/stores/builderStore';
import { api } from '@/lib/api';
import { cn } from '@mawkaa/ui';

export function PagesList() {
  const { toast } = useToast();
  const { site, pages, currentPage, loadPage } = useBuilderStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newPageName, setNewPageName] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreatePage = async () => {
    if (!site || !newPageName) return;

    setIsCreating(true);
    try {
      const response = await api.post<{ page: any }>(`/sites/${site.id}/pages`, {
        name: newPageName,
        slug: newPageSlug || newPageName.toLowerCase().replace(/\s+/g, '-'),
      });

      // Reload site to get updated pages
      await loadPage(response.page.id);
      toast({ title: 'Page créée', description: 'La nouvelle page a été ajoutée.' });
      setIsDialogOpen(false);
      setNewPageName('');
      setNewPageSlug('');
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de créer la page.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!site) return;

    try {
      await api.delete(`/sites/${site.id}/pages/${pageId}`);
      toast({ title: 'Page supprimée' });
      // Load homepage after deletion
      const homepage = pages.find((p) => p.isHomepage && p.id !== pageId);
      if (homepage) {
        await loadPage(homepage.id);
      }
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer la page.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="border-b">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-muted-foreground">Pages</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-1">
          {pages.map((page) => (
            <div
              key={page.id}
              className={cn(
                'flex items-center justify-between rounded-lg px-2 py-1.5 cursor-pointer',
                currentPage?.id === page.id
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-gray-100'
              )}
              onClick={() => loadPage(page.id)}
            >
              <div className="flex items-center gap-2">
                {page.isHomepage ? (
                  <Home className="w-4 h-4" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
                <span className="text-sm">{page.name}</span>
              </div>
              {!page.isHomepage && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>
                      <Edit2 className="w-4 h-4 mr-2" />
                      Renommer
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePage(page.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Create page dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom de la page</Label>
              <Input
                placeholder="À propos"
                value={newPageName}
                onChange={(e) => setNewPageName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>URL (slug)</Label>
              <Input
                placeholder="a-propos"
                value={newPageSlug}
                onChange={(e) => setNewPageSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreatePage} loading={isCreating} disabled={!newPageName}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
